import { useState } from "react";
import {
  useGetCategoriesQuery,
  useToggleCategoryStatusMutation,
} from "../../features/categories/categoryApi";
import {
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
} from "../../features/categories/categoryApi";
import Table from "../../components/list/Table";
import Pagination from "../../components/list/Pagination";
import type { PaginationState } from "@tanstack/react-table";
import SlideInModal from "../../components/modals/SlideInModal";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import FormInput from "../../components/forms/FormInput";
import { FiEdit3, FiPlus } from "react-icons/fi";
import type { ProductCategory } from "../../features/interfaces";
import GridLoader from "react-spinners/GridLoader";

const filterFormLabelClass = "text-sm font-semibold text-gray-800 mb-2 block";
const filterFormErrorClass = "text-rose-500 text-xs mt-1.5";
const filterFormInputClass =
  "w-full py-2.5 px-4 border border-gray-200 rounded-lg text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-[#dbb685] focus:border-[#dbb685] transition-all shadow-sm hover:border-gray-300";

const categorySchema = yup.object({
  name: yup.string().required("Name is required"),
  description: yup.string().nullable().notRequired(),
});

type CategoryFormData = yup.InferType<typeof categorySchema>;

const Categories = () => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] =
    useState<ProductCategory | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [categoryToToggle, setCategoryToToggle] = useState<ProductCategory | null>(null);

  const { data: categoriesResponse, isLoading, isFetching } =
    useGetCategoriesQuery();
  const [createCategory, { isLoading: isCreating }] =
    useCreateCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] =
    useUpdateCategoryMutation();
  const [toggleStatus] = useToggleCategoryStatusMutation();

  const categories = categoriesResponse?.data || [];

  const methods = useForm<CategoryFormData>({
    resolver: yupResolver(categorySchema) as any,
    defaultValues: {
      name: "",
      description: null,
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = methods;

  const handleOpenModal = (category?: ProductCategory) => {
    if (category) {
      setEditingCategory(category);
      reset({
        name: category.name,
        description: category.description || "",
      });
    } else {
      setEditingCategory(null);
      reset({
        name: "",
        description: "",
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCategory(null);
    reset();
  };

  const onSubmit = async (data: CategoryFormData) => {
    try {
      const categoryData: Partial<ProductCategory> = {
        name: data.name,
        description: data.description || undefined,
        is_active: true,
      };

      if (editingCategory?.category_id) {
        await updateCategory({
          id: editingCategory.category_id,
          data: categoryData,
        }).unwrap();
      } else {
        await createCategory(categoryData).unwrap();
      }
      handleCloseModal();
    } catch (error) {
      console.error("Error saving category:", error);
    }
  };

  const handleToggleClick = (category: ProductCategory) => {
    setCategoryToToggle(category);
    setShowConfirmModal(true);
  };

  const handleConfirmToggle = async () => {
    if (categoryToToggle?.category_id) {
      try {
        await toggleStatus({
          id: categoryToToggle.category_id,
          is_active: !categoryToToggle.is_active,
        }).unwrap();
        setShowConfirmModal(false);
        setCategoryToToggle(null);
      } catch (error) {
        console.error("Error toggling status:", error);
        setShowConfirmModal(false);
        setCategoryToToggle(null);
      }
    }
  };

  const renderTextCell = (info: any) => {
    return <span className="text-gray-600 font-medium">{info.getValue() || <span className="text-gray-400">-</span>}</span>;
  };

  const renderStatusCell = (info: any) => {
    const isActive = info.getValue();
    const category = info.row.original as ProductCategory;
    return (
      <div className="flex justify-center">
        <button
          type="button"
          onClick={() => handleToggleClick(category)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#dbb685] focus:ring-offset-2 ${
            isActive ? 'bg-[#dbb685]' : 'bg-gray-300'
          }`}
          role="switch"
          aria-checked={isActive}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              isActive ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    );
  };

  const renderActions = ({ row }: any) => {
    const category = row.original as ProductCategory;
    return (
      <div className="flex justify-center items-center gap-1.5">
        <button
          className="px-2 py-1.5 rounded-lg transition-colors flex items-center justify-center gap-1.5 text-blue-600 hover:bg-blue-50 cursor-pointer border border-blue-200 bg-blue-50/50"
          onClick={() => handleOpenModal(category)}
          title="Edit"
        >
          <FiEdit3 size={14} />
          <span className="text-xs font-medium">Edit</span>
        </button>
      </div>
    );
  };

  const columns = [
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
      cell: renderTextCell,
      meta: { align: 'left' }
    },
    {
      id: "description",
      header: "Description",
      accessorKey: "description",
      cell: renderTextCell,
      meta: { align: 'left' }
    },
    {
      id: "is_active",
      header: "Status",
      accessorKey: "is_active",
      cell: renderStatusCell,
      meta: { align: 'center' }
    },
    {
      id: "actions",
      header: "Actions",
      cell: renderActions,
      meta: { align: 'center' }
    },
  ];

  return (
    <>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-xl font-semibold text-gray-600 mb-1">Product Categories</h1>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="px-4 py-2 text-white rounded-lg font-semibold flex items-center justify-center gap-2 text-sm cursor-pointer"
            style={{ 
              minWidth: '140px', 
              minHeight: '38px',
              backgroundColor: '#dbb685',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#c9a570';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#dbb685';
            }}
          >
            <FiPlus size={16} />
            <span>Add Category</span>
          </button>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <GridLoader color="#dbb685" />
            </div>
          ) : (
            <>
              <Table
                columns={columns}
                data={categories}
                isSearchable={true}
                isSortable={true}
                isFetching={isFetching}
                pagination={true}
                paginationState={pagination}
                setPaginationState={setPagination}
                tableClass="w-full"
                tdClass="text-sm py-4 px-6 text-gray-700 font-medium"
                theadRowClass="bg-gray-100 border-b border-gray-200"
              />
              <div className="mt-6 flex justify-between items-center bg-white rounded-lg border border-gray-200 p-4 mx-4 mb-4">
                <div className="text-sm text-gray-600">
                  Showing <span className="font-semibold text-gray-800">{(pagination.pageIndex * pagination.pageSize) + 1}</span> to{' '}
                  <span className="font-semibold text-gray-800">
                    {Math.min((pagination.pageIndex + 1) * pagination.pageSize, categories.length)}
                  </span>{' '}
                  of <span className="font-semibold text-gray-800">{categories.length}</span> categories
                </div>
                <Pagination
                  currentPage={pagination.pageIndex + 1}
                  totalItems={categories.length}
                  itemsPerPage={pagination.pageSize}
                  onPageChange={(page: number) => {
                    setPagination({
                      ...pagination,
                      pageIndex: page - 1,
                    });
                  }}
                  btnClass="px-4 py-2 border border-gray-300 text-sm text-gray-700 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all font-medium"
                  pageNumClass="text-sm px-3 py-1"
                  activePageNumClass="text-white rounded-lg font-semibold"
                  activePageNumStyle={{ backgroundColor: "#dbb685" }}
                  inactivePageNumClass="hover:bg-gray-100 rounded-lg text-gray-700 font-medium"
                />
              </div>
            </>
          )}
        </div>
      </div>

      <SlideInModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingCategory ? "Edit Category" : "Add Category"}
        width="600px"
      >
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit as any)} className="space-y-4">
            <FormInput
              label="Name"
              name="name"
              placeholder="Enter category name"
              register={register}
              errors={errors}
              labelClassName={filterFormLabelClass}
              className={filterFormInputClass}
              errorClassName={filterFormErrorClass}
            />

            <FormInput
              label="Description"
              name="description"
              type="textarea"
              placeholder="Enter description"
              register={register}
              errors={errors}
              labelClassName={filterFormLabelClass}
              className={filterFormInputClass}
              errorClassName={filterFormErrorClass}
            />

            <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 mt-6">
              <button
                type="button"
                onClick={handleCloseModal}
                className="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg transition-colors shadow-sm"
                style={{ minWidth: '100px', minHeight: '40px' }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                  e.currentTarget.style.borderColor = '#9ca3af';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.borderColor = '#d1d5db';
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isCreating || isUpdating}
                className="px-6 py-2.5 text-sm font-semibold text-white rounded-lg shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                style={{ 
                  minWidth: '120px', 
                  minHeight: '40px',
                  backgroundColor: '#dbb685',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!isCreating && !isUpdating) {
                    e.currentTarget.style.backgroundColor = '#c9a570';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isCreating && !isUpdating) {
                    e.currentTarget.style.backgroundColor = '#dbb685';
                  }
                }}
              >
                {isCreating || isUpdating ? (
                  <span>Saving...</span>
                ) : (
                  <span>{editingCategory ? "Update" : "Create"}</span>
                )}
              </button>
            </div>
          </form>
        </FormProvider>
      </SlideInModal>

      {/* Confirmation Modal for Toggle Status */}
      {showConfirmModal && categoryToToggle && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-[9998]"
            onClick={() => {
              setShowConfirmModal(false);
              setCategoryToToggle(null);
            }}
          ></div>
          <div className="fixed inset-0 flex items-center justify-center z-[9999]">
            <div className="bg-white rounded-lg border border-gray-200 shadow-lg p-6 max-w-md w-full mx-4">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {categoryToToggle.is_active ? "Deactivate Category" : "Activate Category"}
              </h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to {categoryToToggle.is_active ? "deactivate" : "activate"} the category{" "}
                <span className="font-semibold text-gray-900">"{categoryToToggle.name}"</span>? 
                {categoryToToggle.is_active 
                  ? " This category will no longer be available for selection." 
                  : " This category will become available for selection."}
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowConfirmModal(false);
                    setCategoryToToggle(null);
                  }}
                  className="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg transition-colors"
                  style={{ minWidth: '100px', minHeight: '40px' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                    e.currentTarget.style.borderColor = '#9ca3af';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                    e.currentTarget.style.borderColor = '#d1d5db';
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmToggle}
                  className="px-6 py-2.5 text-sm font-semibold text-white rounded-lg transition-colors"
                  style={{ 
                    minWidth: '120px', 
                    minHeight: '40px',
                    backgroundColor: categoryToToggle.is_active ? '#dc2626' : '#10b981',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = categoryToToggle.is_active ? '#b91c1c' : '#059669';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = categoryToToggle.is_active ? '#dc2626' : '#10b981';
                  }}
                >
                  {categoryToToggle.is_active ? "Deactivate" : "Activate"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Categories;

